import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useFrame } from "react-frame-component";

const CHART_JS_URL = "https://cdn-na.seatsio.net/chart.js";

const loadSeatsioInto = (frameWindow, frameDocument) => {
  if (frameWindow.seatsio) return Promise.resolve(frameWindow.seatsio);
  if (frameWindow.__seatsioLoading) return frameWindow.__seatsioLoading;

  frameWindow.__seatsioLoading = new Promise((resolve, reject) => {
    const script = frameDocument.createElement("script");

    script.onload = () => {
      frameWindow.seatsio.region = "na";
      resolve(frameWindow.seatsio);
    };
    script.onerror = () => {
      frameWindow.__seatsioLoading = null;
      reject(new Error("Could not load " + CHART_JS_URL));
    };
    script.src = CHART_JS_URL;
    frameDocument.head.appendChild(script);
  });

  return frameWindow.__seatsioLoading;
};

const RealmSafeSeatsioChart = (props) => {
  const frame = useFrame();
  const container = useRef(null);
  const chartRef = useRef(null);
  const insideFrame = Boolean(frame) && Boolean(frame.document) && frame.document !== document;
  const framedDocument = insideFrame ? frame.document : document;
  const framedWindow = insideFrame ? frame.window : window;

  const { onRenderStarted, region, divId, ...config } = props;
  const configKey = JSON.stringify(config);

  useEffect(() => {
    let destroyed = false;

    loadSeatsioInto(framedWindow, framedDocument)
      .then((seatsio) => {
        if (destroyed) return;
        if (!container.current) return;

        const chart = new seatsio.SeatingChart({ ...config, container: container.current }).render();

        chartRef.current = chart;
        if (onRenderStarted) onRenderStarted(chart);
      })
      .catch((error) => {
        console.warn("seats.io chart failed", error);
        if (window.Sentry) window.Sentry.captureException(error);
      });

    return () => {
      destroyed = true;
      if (!chartRef.current) return;
      if (chartRef.current.state === "DESTROYED") return;

      chartRef.current.destroy();
      chartRef.current = null;
    };
  }, [framedDocument, framedWindow, configKey]);

  return <div ref={container} style={{ height: "100%" }} />;
};

RealmSafeSeatsioChart.propTypes = {
  workspaceKey: PropTypes.string.isRequired,
  event: PropTypes.string.isRequired,
  onRenderStarted: PropTypes.func,
  region: PropTypes.string,
  divId: PropTypes.string
};

export default RealmSafeSeatsioChart;
